import { useState } from "react";
import { Siren, X, Phone, Radio, AlertTriangle, ShieldAlert, ClipboardList, ArrowRight, CheckCircle2, Waves, HeartPulse, Zap, Wind, Anchor, Users, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface FloatingAccidentSimulationProps {
  position?: "bottom-right" | "bottom-left";
}

type Step = {
  key: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  actions: string[];
};

const STEPS: Step[] = [
  {
    key: "proteger",
    title: "1. PROTÉGER",
    icon: ShieldAlert,
    color: "text-amber-400",
    actions: [
      "Couper le moteur du jet impliqué (lanière coupe-circuit).",
      "Stopper l'activité du groupe — rassembler les autres élèves à distance.",
      "Sécuriser la zone : balisage visuel, éloigner les autres engins.",
      "Mettre la victime en sécurité hors de l'eau si possible (sur le jet ou à bord).",
    ],
  },
  {
    key: "alerter",
    title: "2. ALERTER",
    icon: Radio,
    color: "text-red-400",
    actions: [
      "VHF Canal 16 : MAYDAY MAYDAY MAYDAY → CROSS.",
      "Téléphone : 196 (CROSS) — gratuit depuis un mobile en mer.",
      "Préciser : position GPS, nature de l'accident, nombre de victimes, état, conditions météo.",
      "Prévenir IMMÉDIATEMENT la base nautique / responsable d'exploitation.",
    ],
  },
  {
    key: "secourir",
    title: "3. SECOURIR (PSC1)",
    icon: ShieldAlert,
    color: "text-emerald-400",
    actions: [
      "Bilan vital : conscience, ventilation, circulation.",
      "PLS si inconscient et respire. RCP si arrêt cardio-respiratoire.",
      "Suspicion traumatique (chute, choc) : NE PAS mobiliser la nuque, maintien tête.",
      "Hypothermie : couvrir, isoler du vent, surveiller en continu jusqu'aux secours.",
    ],
  },
  {
    key: "consigner",
    title: "4. CONSIGNER",
    icon: ClipboardList,
    color: "text-sky-400",
    actions: [
      "Rédiger un rapport d'accident détaillé (heure, lieu, circonstances, témoins).",
      "Photos de la zone, du matériel, des dommages.",
      "Recueillir les coordonnées des témoins et des victimes.",
      "Déclaration assurance sous 5 jours ouvrés. Déclaration AT employeur sous 48 h.",
    ],
  },
];

const CONTACTS = [
  { label: "CROSS (Urgence Mer)", value: "196", icon: Phone, tone: "destructive" },
  { label: "VHF de détresse", value: "Canal 16", icon: Radio, tone: "destructive" },
  { label: "SAMU", value: "15", icon: Phone, tone: "default" },
  { label: "Pompiers", value: "18", icon: Phone, tone: "default" },
  { label: "Urgences européennes", value: "112", icon: Phone, tone: "default" },
  { label: "Base nautique / Exploitant", value: "À pré-enregistrer", icon: Phone, tone: "secondary" },
  { label: "Assurance RC Pro moniteur", value: "À pré-enregistrer", icon: Phone, tone: "secondary" },
];

// Scénarios d'accidents typiques en randonnée jet-ski
const SCENARIOS = [
  {
    key: "chute-vitesse",
    title: "Chute à grande vitesse",
    icon: Zap,
    color: "text-amber-400",
    risk: "Choc à la surface, traumatismes, perte de connaissance, lavage haute pression.",
    actions: [
      "Couper le moteur (la lanière coupe-circuit doit déjà avoir agi).",
      "Approcher le pilote face au vent, moteur coupé à 5 m.",
      "Vérifier conscience et respiration AVANT de remettre sur le jet.",
      "Suspicion de lésion périnéale → évacuation médicale obligatoire.",
    ],
  },
  {
    key: "collision",
    title: "Collision entre deux jets",
    icon: AlertTriangle,
    color: "text-red-400",
    risk: "Polytraumatismes, fractures, plaies, noyade secondaire.",
    actions: [
      "Stopper TOUT le groupe — rassembler à 30 m de la zone.",
      "Bilan des deux victimes, priorité à l'inconscient.",
      "MAYDAY Canal 16 immédiat + 196.",
      "Photos de la position des engins AVANT de les déplacer (assurance).",
    ],
  },
  {
    key: "noyade",
    title: "Noyade / immersion prolongée",
    icon: Waves,
    color: "text-sky-400",
    risk: "Hypoxie, arrêt cardio-respiratoire, hypothermie.",
    actions: [
      "Sortir la victime de l'eau au plus vite (jet ou bateau).",
      "Libérer voies aériennes, bilan ventilation.",
      "RCP 30/2 si arrêt — défibrillateur dès que disponible.",
      "Toujours évacuer même si la victime semble récupérée (noyade secondaire à 24 h).",
    ],
  },
  {
    key: "hypothermie",
    title: "Hypothermie (eau froide / vent)",
    icon: Wind,
    color: "text-cyan-400",
    risk: "Frissons, confusion, perte de coordination, arrêt cardiaque.",
    actions: [
      "Sortir de l'eau, retirer combinaison mouillée.",
      "Envelopper dans couverture de survie (face dorée vers la peau).",
      "Boissons sucrées tièdes SI conscient — jamais d'alcool.",
      "Ne pas frictionner — mobilisation douce, position assise dos calé.",
    ],
  },
  {
    key: "helice",
    title: "Blessure par hélice / pompe",
    icon: Anchor,
    color: "text-rose-400",
    risk: "Plaies profondes, hémorragies, section tendineuse.",
    actions: [
      "Couper IMMÉDIATEMENT le moteur (lanière).",
      "Compression directe sur la plaie avec tissu propre.",
      "Garrot tourniquet en dernier recours (membre uniquement, noter l'heure).",
      "Évacuation SAMU/SNSM — ne pas retirer un corps étranger enfoncé.",
    ],
  },
  {
    key: "panne",
    title: "Panne moteur / dérive",
    icon: ShieldAlert,
    color: "text-yellow-400",
    risk: "Dérive vers zones dangereuses, collision avec autre trafic, fatigue.",
    actions: [
      "Mettre le jet à l'ancre flottante si dispo, signalement visuel (gilet orange).",
      "Regrouper les élèves autour du jet en panne.",
      "Contacter la base par VHF/portable — donner position GPS.",
      "Pas de remorquage entre jets non équipés (interdit) — attendre assistance.",
    ],
  },
  {
    key: "malaise",
    title: "Malaise élève (insolation, déshydratation, panique)",
    icon: HeartPulse,
    color: "text-pink-400",
    risk: "Perte d'équilibre, chute, aggravation rapide en mer.",
    actions: [
      "Mettre la victime à l'abri (bateau d'assistance ou retour base).",
      "Ombre, hydratation, allongée jambes surélevées.",
      "Crise de panique : voix calme, respiration guidée, contact visuel.",
      "Doute → 15 (SAMU) pour avis médical.",
    ],
  },
  {
    key: "perte-eleve",
    title: "Élève disparu du groupe",
    icon: Users,
    color: "text-orange-400",
    risk: "Perte de vue, dérive, accident non vu.",
    actions: [
      "Stopper le groupe, comptage immédiat, dernière position connue.",
      "Recherche en étoile à partir du point de disparition.",
      "Au-delà de 5 min sans contact visuel → 196 + CROSS, ne pas attendre.",
      "Toujours partir avec un nombre maximum d'élèves défini par l'arrêté (6 max + moniteur).",
    ],
  },
];


export const AccidentCard = () => {
  const [doneSteps, setDoneSteps] = useState<Record<string, boolean>>({});
  const toggleStep = (key: string) =>
    setDoneSteps((s) => ({ ...s, [key]: !s[key] }));
  const progress = Object.values(doneSteps).filter(Boolean).length;

  return (
    <Card className="w-full max-h-[80vh] overflow-y-auto shadow-2xl border-orange-500/30 bg-gradient-to-br from-background to-background/95">
      <CardHeader className="pb-3 sticky top-0 bg-background/95 backdrop-blur-sm z-10 border-b border-border/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
            <Siren className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-base text-orange-400">Simulation accident</CardTitle>
            <p className="text-xs text-muted-foreground">Protocole moniteur — Que faire ? Qui appeler ?</p>
          </div>
        </div>
        {progress > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <div className="flex-1 h-1.5 bg-secondary/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                style={{ width: `${(progress / STEPS.length) * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground">{progress}/{STEPS.length}</span>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4 pt-4">
        <div className="rounded-lg bg-orange-500/10 border border-orange-500/20 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-orange-400">Méthode P.A.S.</p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                <strong>Protéger</strong> → <strong>Alerter</strong> → <strong>Secourir</strong>.
                Ne jamais inverser l'ordre. Toute action doit être pensée pour ne pas créer une
                nouvelle victime (vous-même compris).
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Phone className="h-4 w-4 text-red-400" />
            Qui contacter ?
          </h4>
          <div className="space-y-1.5">
            {CONTACTS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  className={`flex items-center gap-2 p-2 rounded-md text-xs ${
                    c.tone === "destructive"
                      ? "bg-red-500/10 border border-red-500/30"
                      : "bg-secondary/30"
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${c.tone === "destructive" ? "text-red-400" : "text-muted-foreground"}`} />
                  <span className="text-muted-foreground flex-1">{c.label}</span>
                  <Badge
                    variant={c.tone === "destructive" ? "default" : "outline"}
                    className={`text-[10px] h-5 font-mono ${
                      c.tone === "destructive" ? "bg-red-500 hover:bg-red-500" : ""
                    }`}
                  >
                    {c.value}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-orange-400" />
            Accidents typiques en randonnée jet-ski
          </h4>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Cliquez un scénario pour voir le risque et la conduite à tenir.
          </p>
          <div className="space-y-1.5">
            {SCENARIOS.map((sc) => {
              const Icon = sc.icon;
              return (
                <details
                  key={sc.key}
                  className="group rounded-lg border border-border/40 bg-secondary/20 open:bg-secondary/30 open:border-orange-500/30 transition-colors"
                >
                  <summary className="flex items-center gap-2 p-2.5 cursor-pointer list-none">
                    <Icon className={`h-4 w-4 shrink-0 ${sc.color}`} />
                    <span className="text-xs font-medium flex-1">{sc.title}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-2">
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-semibold text-orange-400">Risque : </span>
                      {sc.risk}
                    </p>
                    <div>
                      <p className="text-[11px] font-semibold text-emerald-400 mb-1">Conduite à tenir :</p>
                      <ul className="space-y-1">
                        {sc.actions.map((a, i) => (
                          <li key={i} className="text-[11px] text-muted-foreground leading-relaxed flex items-start gap-1.5">
                            <span className="text-primary mt-0.5">•</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            Protocole étape par étape
          </h4>
          {STEPS.map((step) => {
            const Icon = step.icon;
            const done = !!doneSteps[step.key];
            return (
              <div
                key={step.key}
                className={`rounded-lg border p-3 transition-all ${
                  done ? "bg-emerald-500/10 border-emerald-500/30" : "bg-secondary/20 border-border/40"
                }`}
              >
                <button
                  onClick={() => toggleStep(step.key)}
                  className="w-full flex items-center gap-2 mb-2"
                >
                  <Icon className={`h-4 w-4 shrink-0 ${done ? "text-emerald-400" : step.color}`} />
                  <span className={`text-sm font-semibold flex-1 text-left ${done ? "text-emerald-400 line-through" : ""}`}>
                    {step.title}
                  </span>
                  {done && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
                </button>
                <ul className="space-y-1 pl-6">
                  {step.actions.map((a, i) => (
                    <li key={i} className="text-xs text-muted-foreground leading-relaxed flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3">
          <p className="text-xs font-semibold text-red-400 flex items-center gap-2 mb-2">
            <Radio className="h-4 w-4" /> Message MAYDAY type
          </p>
          <pre className="text-[11px] text-muted-foreground leading-relaxed font-mono whitespace-pre-wrap">
{`MAYDAY MAYDAY MAYDAY
Ici [nom base / indicatif] × 3
MAYDAY [nom base]
Position : [LAT / LON ou repère]
Nature : accident jet-ski, [nb] victime(s)
État : conscient / inconscient / blessé
Assistance demandée : évacuation médicale
À vous.`}
          </pre>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button asChild variant="destructive" size="sm" className="text-xs">
            <a href="tel:196">
              <Phone className="h-3.5 w-3.5 mr-1" /> Appeler 196
            </a>
          </Button>
          <Button asChild variant="outline" size="sm" className="text-xs border-red-500/30">
            <a href="tel:112">
              <Phone className="h-3.5 w-3.5 mr-1" /> Appeler 112
            </a>
          </Button>
        </div>

        <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
          <div className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-primary">Next Step</p>
              <p className="text-xs text-muted-foreground mt-1">
                Pré-enregistrer DÈS AUJOURD'HUI les numéros de la base nautique, de l'assurance
                RC Pro et du référent sécurité dans le téléphone de service.
              </p>
            </div>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground text-center pt-1 border-t border-border/30">
          Outil pédagogique — Ne remplace pas la formation PSC1 ni le protocole interne de votre base.
        </p>
      </CardContent>
    </Card>
  );
};

export const FloatingAccidentSimulation = ({ position = "bottom-left" }: FloatingAccidentSimulationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const posClasses = position === "bottom-right" ? "right-4 bottom-20" : "left-4 bottom-4";

  return (
    <div className={`fixed ${posClasses} z-40`}>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-110 transition-all duration-300 animate-pulse"
          aria-label="Simulation accident moniteur"
        >
          <Siren className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-background">
            !
          </span>
          <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-background/95 backdrop-blur-sm border border-orange-500/30 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl">
            <p className="text-sm font-medium text-orange-400">Simulation accident</p>
            <p className="text-xs text-muted-foreground">Que faire ? Qui appeler ?</p>
          </div>
        </button>
      )}

      {isOpen && (
        <div className="animate-in fade-in zoom-in-95 duration-200 relative w-[360px] sm:w-[440px]">
          <button
            onClick={() => setIsOpen(false)}
            className="absolute right-3 top-3 z-20 p-1.5 rounded-full hover:bg-secondary/50 transition-colors bg-background/80"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
          <AccidentCard />
        </div>
      )}
    </div>
  );
};
